import { passClick } from '../lib/mapPass';
import { useEffect } from 'react';
import L from 'leaflet';
import { LINE_STYLES, segLen, lineLen, ensurePatterns } from '../lib/drawStyles';

const esc = (s) => String(s || '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
const ang = (a, b) => (Math.atan2(-(b[0] - a[0]), (b[1] - a[1]) * Math.cos((a[0] * Math.PI) / 180)) * 180) / Math.PI;
const mid = (a, b) => [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
const icon = (html, cls = 'ds-ic') => L.divIcon({ className: 'ds-wrap', html: `<span class="${cls}">${html}</span>`, iconSize: [0, 0] });
const fmt = (v) => `${v.toFixed(2).replace('.', ',')} m`;

// symbols every ~step metres along a line (fence ×, flow ▸)
function along(g, P, step, html, rot, pane) {
  for (let i = 0; i + 1 < P.length; i++) {
    const L0 = segLen(P[i], P[i + 1]), n = Math.max(1, Math.floor(L0 / step)), deg = ang(P[i], P[i + 1]);
    for (let k = 1; k <= n; k++) { const u = k / (n + 1); g.addLayer(L.marker([P[i][0] + (P[i + 1][0] - P[i][0]) * u, P[i][1] + (P[i + 1][1] - P[i][1]) * u], { pane, interactive: false, icon: icon(html, `ds-sym" style="transform:translate(-50%,-50%) rotate(${rot ? deg : 0}deg)`) })); }
  }
}

function addOne(g, d, onSel, pane) {
  const plan = d.status !== 'built', P = d.pts, c = LINE_STYLES[d.style] || '#0f172a', click = (l) => l.on('click', (e) => { L.DomEvent.stopPropagation(e); if (!window.__gissDrafting) onSel(d.id); else passClick(e); });
  const o = { pane, opacity: plan ? 0.8 : 1 };
  if (d.kind === 'area') {
    g.addLayer(click(L.polygon(P, { ...o, color: plan ? '#7c3aed' : '#0f172a', weight: 2, dashArray: plan ? '8 6' : null, fillColor: `url(#pat-${d.style})`, fillOpacity: 1, className: `ds-area ds-${d.id}` })));
  } else if (d.kind === 'line') {
    if (d.style === 'wall') { g.addLayer(click(L.polyline(P, { ...o, color: c, weight: 9 }))); g.addLayer(L.polyline(P, { ...o, color: '#fff', weight: 4, interactive: false })); }
    else g.addLayer(click(L.polyline(P, { ...o, color: c, weight: d.style === 'hedge' ? 7 : 3, dashArray: d.style === 'dashed' ? '10 6' : d.style === 'drain' ? '12 4 2 4' : d.style === 'hedge' ? '1 9' : d.style === 'fence' ? '6 4' : null, lineCap: 'round', className: `ds-line ds-${d.id}` })));
    if (d.style === 'fence') along(g, P, 3, '×', false, pane);
    if (d.style === 'flow') along(g, P, 8, '▸', true, pane);
    if (plan) g.addLayer(L.polyline(P, { pane, color: '#7c3aed', weight: 1.5, dashArray: '4 6', interactive: false }));
  } else if (d.kind === 'dim' && P.length > 1) {
    const [a, b] = P, deg = ang(a, b), txt = d.text || fmt(segLen(a, b));
    g.addLayer(click(L.polyline([a, b], { pane, color: '#be123c', weight: 1.5 })));
    [a, b].forEach((p, i) => g.addLayer(L.marker(p, { pane, interactive: false, icon: icon(i ? '▶' : '◀', `ds-sym ds-dimarrow" style="transform:translate(-50%,-50%) rotate(${deg}deg)`) })));
    g.addLayer(L.marker(mid(a, b), { pane, icon: icon(esc(txt), `ds-dimtxt" style="transform:translate(-50%,-120%) rotate(${deg > 90 || deg < -90 ? deg + 180 : deg}deg)`) }).on('click', () => onSel(d.id)));
  } else if (d.kind === 'note' && P.length > 1) {
    g.addLayer(L.polyline(P, { pane, color: '#0f172a', weight: 1.5, interactive: false }));
    g.addLayer(L.marker(P[0], { pane, interactive: false, icon: icon('●', 'ds-sym ds-tip') }));
    g.addLayer(L.marker(P[P.length - 1], { pane, icon: icon(esc(d.text || '…'), 'ds-note') }).on('click', () => onSel(d.id)));
  }
  if (plan && (d.kind === 'line' || d.kind === 'area') && P.length > 1) g.addLayer(L.marker(d.kind === 'area' ? L.polygon(P).getBounds().getCenter() : mid(P[0], P[1]), { pane, interactive: false, icon: icon('PLAN', 'ds-plan') }));
}

export function useDrawLayer(mapRef, drawings, draft, onSel) {
  useEffect(() => {
    const map = mapRef.current;
    if (!map || (!drawings?.length && !draft)) return undefined;
    ensurePatterns();
    if (!map.getPane('drawings')) map.createPane('drawings').style.zIndex = 430;
    const g = L.layerGroup().addTo(map);
    (drawings || []).forEach((d) => addOne(g, d, onSel, 'drawings'));
    if (draft?.pts.length) {
      addOne(g, { ...draft, id: 'draft', status: 'plan' }, () => {}, 'drawings');
      draft.pts.forEach((p) => g.addLayer(L.circleMarker(p, { pane: 'drawings', radius: 5, color: '#7c3aed', fillColor: '#fff', fillOpacity: 1, weight: 2, interactive: false })));
      if (draft.kind === 'line' && draft.pts.length > 1) g.addLayer(L.marker(draft.pts[draft.pts.length - 1], { pane: 'drawings', interactive: false, icon: icon(fmt(lineLen(draft.pts)), 'ds-dimtxt') }));
    }
    return () => g.remove();
  }, [mapRef, drawings, draft, onSel]);
}
